import { computed, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import type { ApiError } from "@/helpers/api-error";
import type { PartnerClient } from "@/types/project-types";

/**
 * Query hook + dashboard aggregation for a partner's client organisations. Sources the rich
 * `/partners/clients` summaries (real project counts + run metrics, server-scoped to the calling
 * partner), shared by the Partners dashboard (stat cards), the Clients list, and the client detail
 * page so all three render the same authoritative numbers.
 */

const BASE = `${import.meta.env.VITE_API_URL}/partners/clients`;

/** Shape returned by the API (PartnerClientResponse). */
interface PartnerClientRow {
  id: string;
  partnershipId: string;
  organizationName: string;
  contactName: string;
  contactEmail: string;
  status: string;
  joinedDate: string;
  lastActivity: string;
  projectCount: number;
  totalRuns: number;
  successRate: number;
  healthStatus: string;
  isPending: boolean;
}

function toPartnerClient(row: PartnerClientRow): PartnerClient {
  return {
    id: row.id,
    partnershipId: row.partnershipId,
    organizationName: row.organizationName,
    contactName: row.contactName || "N/A",
    contactEmail: row.contactEmail || "N/A",
    status: (row.status as PartnerClient["status"]) || "active",
    joinedDate: row.joinedDate,
    lastActivity: row.lastActivity,
    projectCount: row.projectCount,
    totalRuns: row.totalRuns,
    successRate: row.successRate,
    healthStatus: (row.healthStatus as PartnerClient["healthStatus"]) || "healthy",
    issues: [],
    isPending: row.isPending,
  };
}

/**
 * Fetch the caller-partner's client organisations with real metrics. The server scopes to the
 * authenticated partner — no org id is passed.
 */
export function usePartnerClientSummaries() {
  const { data, isFetching, error, refetch } = useQuery({
    queryKey: ["partner-client-summaries"],
    queryFn: async () =>
      (await fetchWrapper.get<PartnerClientRow[]>(BASE)).map(toPartnerClient),
  });

  const clients = computed(() => data.value ?? []);

  return {
    clients,
    clientsLoading: isFetching,
    clientsError: error as Ref<ApiError | null>,
    refetch,
  };
}

/** Fleet-wide dashboard rollups, derived from the client summaries. */
export interface PartnerClientRollup {
  totalClients: number;
  activeClients: number;
  totalProjects: number;
  totalRuns: number;
  /** Run-weighted success rate across all clients (an average-of-averages would skew small clients). */
  avgSuccessRate: number;
  successfulRuns: number;
}

export function rollupClientSummaries(clients: PartnerClient[]): PartnerClientRollup {
  const totalClients = clients.length;
  const activeClients = clients.filter((c) => c.status === "active").length;
  const totalProjects = clients.reduce((sum, c) => sum + c.projectCount, 0);
  const totalRuns = clients.reduce((sum, c) => sum + c.totalRuns, 0);

  // Weight each client's rate by its run count so the fleet rate reflects actual run volume.
  const successfulRuns = clients.reduce(
    (sum, c) => sum + Math.round((c.successRate / 100) * c.totalRuns),
    0,
  );
  const avgSuccessRate = totalRuns > 0 ? Math.round((successfulRuns / totalRuns) * 1000) / 10 : 0;

  return { totalClients, activeClients, totalProjects, totalRuns, avgSuccessRate, successfulRuns };
}
